/**
 * Curated list of {{job.<dotted.path>}} placeholders for the Carrier DG Template
 * inline cell editor. Each entry has:
 *   - label: human-readable name shown in the picker
 *   - token: the literal `{{...}}` string inserted into the cell
 *   - group: section header for the picker
 *
 * Field paths reflect the actual Job document shape in /app/backend/server.py:
 *   - Parties are nested snapshots: shipper_snapshot.name / .address / .gstin / .iec_code
 *   - BL overrides are flat: bl_shipper_name_override / bl_shipper_address_override / …
 *   - Ports & places are flat: pol_name / pod_name / place_of_receipt / place_of_delivery
 *   - DG fields are flat: dg_un_number / dg_imdg_class / …
 *   - Company snapshot is exposed via DocPrintShell as `co.name / .address_line_1 / .gstin / …`
 */

export const PLACEHOLDER_FIELDS = [
  // Job basics
  { group: "Job", label: "Job Number", token: "{{job.job_number}}" },
  { group: "Job", label: "Job Type", token: "{{job.job_type}}" },
  { group: "Job", label: "Mode", token: "{{job.mode}}" },
  { group: "Job", label: "ETD", token: "{{job.etd}}" },
  { group: "Job", label: "ETA", token: "{{job.eta}}" },
  { group: "Job", label: "Sailing Date (SOB)", token: "{{job.sailing_date}}" },
  { group: "Job", label: "Booking Reference", token: "{{job.booking_reference}}" },
  { group: "Job", label: "BL Export Reference", token: "{{job.bl_export_reference}}" },
  { group: "Job", label: "Today's Date (auto)", token: "{{today}}" },

  // Carrier / Vessel
  { group: "Carrier & Vessel", label: "Carrier Name", token: "{{job.carrier_name}}" },
  { group: "Carrier & Vessel", label: "Vessel Name", token: "{{job.vessel_name}}" },
  { group: "Carrier & Vessel", label: "Voyage Number", token: "{{job.voyage_number}}" },
  { group: "Carrier & Vessel", label: "Flight Number", token: "{{job.flight_number}}" },

  // Routing
  { group: "Routing", label: "Place of Receipt", token: "{{job.place_of_receipt}}" },
  { group: "Routing", label: "Port of Loading (POL)", token: "{{job.pol_name}}" },
  { group: "Routing", label: "Port of Discharge (POD)", token: "{{job.pod_name}}" },
  { group: "Routing", label: "Place of Delivery", token: "{{job.place_of_delivery}}" },

  // Parties — nested snapshots
  { group: "Booking Party", label: "Booking Party Name", token: "{{job.booking_party_snapshot.name}}" },
  { group: "Booking Party", label: "Booking Party Address", token: "{{job.booking_party_snapshot.address}}" },
  { group: "Booking Party", label: "Booking Party GSTIN", token: "{{job.booking_party_snapshot.gstin}}" },

  { group: "Shipper", label: "Shipper Name", token: "{{job.shipper_snapshot.name}}" },
  { group: "Shipper", label: "Shipper Address", token: "{{job.shipper_snapshot.address}}" },
  { group: "Shipper", label: "Shipper GSTIN", token: "{{job.shipper_snapshot.gstin}}" },
  { group: "Shipper", label: "Shipper IEC", token: "{{job.shipper_snapshot.iec_code}}" },
  { group: "Shipper", label: "Shipper Name (BL override)", token: "{{job.bl_shipper_name_override}}" },
  { group: "Shipper", label: "Shipper Address (BL override)", token: "{{job.bl_shipper_address_override}}" },

  { group: "Consignee", label: "Consignee Name", token: "{{job.consignee_snapshot.name}}" },
  { group: "Consignee", label: "Consignee Address", token: "{{job.consignee_snapshot.address}}" },
  { group: "Consignee", label: "Consignee GSTIN", token: "{{job.consignee_snapshot.gstin}}" },
  { group: "Consignee", label: "Consignee Name (BL override)", token: "{{job.bl_consignee_name_override}}" },
  { group: "Consignee", label: "Consignee Address (BL override)", token: "{{job.bl_consignee_address_override}}" },

  { group: "Notify", label: "Notify Party Name", token: "{{job.notify_party_snapshot.name}}" },
  { group: "Notify", label: "Notify Party Address", token: "{{job.notify_party_snapshot.address}}" },
  { group: "Notify", label: "Notify Name (BL override)", token: "{{job.bl_notify_name_override}}" },
  { group: "Notify", label: "Notify Address (BL override)", token: "{{job.bl_notify_address_override}}" },

  // Cargo
  { group: "Cargo", label: "Description of Goods", token: "{{job.description_of_goods}}" },
  { group: "Cargo", label: "Containers Booked", token: "{{job.no_of_containers_booked}}" },
  { group: "Cargo", label: "Total Gross Weight (kg)", token: "{{job.total_gross_weight_kg}}" },
  { group: "Cargo", label: "Total Net Weight (kg)", token: "{{job.total_net_weight_kg}}" },
  { group: "Cargo", label: "Total Volume (CBM)", token: "{{job.total_volume_cbm}}" },

  // Dangerous Goods
  { group: "Dangerous Goods", label: "UN Number", token: "{{job.dg_un_number}}" },
  { group: "Dangerous Goods", label: "IMDG Class", token: "{{job.dg_imdg_class}}" },
  { group: "Dangerous Goods", label: "IATA DGR Class", token: "{{job.dg_iata_dgr_class}}" },
  { group: "Dangerous Goods", label: "Packing Group", token: "{{job.dg_packing_group}}" },
  { group: "Dangerous Goods", label: "Proper Shipping Name", token: "{{job.dg_proper_shipping_name}}" },
  { group: "Dangerous Goods", label: "Technical Name", token: "{{job.dg_technical_name}}" },
  { group: "Dangerous Goods", label: "EmS Number", token: "{{job.dg_ems_no}}" },
  { group: "Dangerous Goods", label: "MFAG Table No.", token: "{{job.dg_mfag_table_no}}" },
  { group: "Dangerous Goods", label: "Flashpoint (°C)", token: "{{job.dg_flashpoint_c}}" },
  { group: "Dangerous Goods", label: "Marine Pollutant (Y/N)", token: "{{job.dg_marine_pollutant}}" },
  { group: "Dangerous Goods", label: "Emergency Contact", token: "{{job.dg_emergency_contact}}" },
  { group: "Dangerous Goods", label: "DG Notes", token: "{{job.dg_notes}}" },

  // Company (issuer)
  { group: "Issuing Company", label: "Company Name", token: "{{co.name}}" },
  { group: "Issuing Company", label: "Company Address Line 1", token: "{{co.address_line_1}}" },
  { group: "Issuing Company", label: "City", token: "{{co.city}}" },
  { group: "Issuing Company", label: "State", token: "{{co.state}}" },
  { group: "Issuing Company", label: "Country", token: "{{co.country}}" },
  { group: "Issuing Company", label: "GSTIN", token: "{{co.gstin}}" },
  { group: "Issuing Company", label: "IEC Code", token: "{{co.iec}}" },
];

export const PLACEHOLDER_GROUPS = Array.from(new Set(PLACEHOLDER_FIELDS.map((f) => f.group)));
